<template>
  <div>
    <BaseDialog
        title="移動指示"
        width="60%"
        :visible.sync="dialogVisible"
        :before-close="close"
        :show-only-close-button="!isNew"
        :disable-ok-button="disableSaveButton"
        @ok="save"
        @cancel="close">
      <template slot="contents">
        <div class="dialog-contents">
          <template>
            <BaseTableDetail
                :table-set-list="_tableSetList1"
                :display-item="detailData"
                class="detail-table"
                :editable="componentEditable" />
            <el-button v-if="isNew"
                :disabled="disabledAddButton"
                class="add-button"
                @click="addRow">
              行追加
            </el-button>
            <BaseTableList
                :table-set-list="_tableSetList2"
                :display-items="stockMoveItems"
                :editable="componentEditable"
                class="list-table margin-top"
                validate-scope="tableList">
              <!-- オペレーションボタン -->
              <template
                  slot="operation"
                  slot-scope="{ row }">
                <BaseOperationTooltip
                    v-if="true"
                    :list="getTooltipList(row.row)"
                    placement="bottom-end" />
              </template>
            </BaseTableList>
          </template>
        </div>
      </template>
    </BaseDialog>
    <BaseDialog
        title="出荷先検索"
        append-to-body
        width="90%"
        :visible.sync="searchdestinationsDialogVisible"
        :show-footer="false">
      <template slot="contents">
        <SearchDestinations
            @destination-selected="destinationSelected" />
      </template>
    </BaseDialog>
    <BaseDialog
        title="商品検索"
        append-to-body
        width="90%"
        :visible.sync="searchSkusDialogVisible"
        :show-footer="false">
      <template slot="contents">
        <SearchSkus
            @sku-selected="skuSelected" />
      </template>
    </BaseDialog>
  </div>
</template>

<style scoped>
.dialog-contents .detail-table ::v-deep table {
  width: 70%;
  border-collapse: collapse;
  border-spacing: 0;
  border: none;
}
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
}
.add-button {
  float: right;
  margin-right: 10px;
}
.dialog-contents .detail-table ::v-deep .padding {
  padding-left: 30px !important;
}
.dialog-contents .detail-table ::v-deep .header {
  width: 20% !important;
}
.dialog-contents .margin-top {
  margin-top: 30px;
}
.dialog-contents .list-table ::v-deep td {
  vertical-align: middle;
}
</style>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import common from '@/utils/common'
import message from '@/utils/message'
import * as define from '@/define'
import * as config from '@/config'
import * as query from '@/queries/stock'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import BaseDialog from '@/components/molecules/BaseDialog'
import SearchSkus from '@/components/pages/order/SearchSkus'
import SearchDestinations from '@/components/pages/stock/SearchDestinations'
import BaseOperationTooltip from '@/components/molecules/BaseOperationTooltip'
import BaseTableList from '@/components/molecules/BaseTableList'

import x from '@/components/mixins/mixin'
import confirmDiscardOnLeave from '@/components/mixins/confirmDiscardOnLeave'

const EDITABLE = Object.freeze({
  [define.STOCK_MOVE_MODE.NORMAL]: Object.freeze({}),
  [define.STOCK_MOVE_MODE.NEW]: Object.freeze({ site: true, name: true, postalCode: true, stateCode: true, city: true,
    address1: true, address2: true, phone: true, reason: true, note: true, preferredShippingAt: true, qty: true })
})
export default {
  name: 'StockMoveInstructDialog',
  components: {
    BaseTableDetail,
    BaseTableList,
    BaseDialog,
    BaseOperationTooltip,
    SearchSkus,
    SearchDestinations
  },
  mixins: [ x, confirmDiscardOnLeave ],
  props: {
    appendToBody: {
      type: Boolean,
      default: false
    },
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: Number,
      default: 0
    },
    uuid: {
      type: String,
      default: ''
    }
  },
  provide () {
    return {
      parentValidator: this.$validator
    }
  },
  data () {
    return {
      dialogVisible: false,
      searchSkusDialogVisible: false,
      searchdestinationsDialogVisible: false,
      detailData: {},
      isChanged: false
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      sites: (state) => state.sites
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    storePath () {
      return 'StockAdjustment'
    },
    componentEditable () {
      return EDITABLE[this.mode]
    },
    _tableSetList1 () {
      return [
        {
          label: '在庫元', headerClass: 'header',
          properties: [
            { path: 'site.code', elementType: ELEMENT_TYPE.SELECT_BOX, type: 'String', codeName: 'sites', validateRule: 'required',
              selectCode: 'sites', validateAs: '対象在庫', editableId: 'site', change: this.siteChange }
          ]
        },
        {
          label: '出荷先',
          properties: [
            { path: 'codeName', elementType: ELEMENT_TYPE.TEXT, type: 'String' },
            {
              path: 'searchSkus', type: 'String', elementType: ELEMENT_TYPE.BUTTON,
              label: '検索', click: this.openSearchdestination, class: { bold: true, right: true },
              disabled: () => this.mode !== define.STOCK_MOVE_MODE.NORMAL
            }
          ],
          horizontal: true
        },
        {
          label: '出荷先名称', headerClass: 'padding',
          properties: [
            { path: 'name', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'required|max:256',
              validateAs: '出荷先名称', editableId: 'name' }
          ]
        },
        {
          label: '郵便番号', headerClass: 'padding',
          properties: [
            { path: 'postalCode', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'required|max:256',
              validateAs: '郵便番号', editableId: 'postalCode' }
          ]
        },
        {
          label: '都道府県', headerClass: 'padding',
          properties: [
            { path: 'stateCode', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'required|max:512',
              validateAs: '都道府県', editableId: 'stateCode' }
          ]
        },
        {
          label: '市区町村', headerClass: 'padding',
          properties: [
            { path: 'city', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'required|max:256',
              validateAs: '市区町村', editableId: 'city' }
          ]
        },
        {
          label: '住所1', headerClass: 'padding',
          properties: [
            { path: 'address1', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'required|max:512',
              validateAs: '住所1', editableId: 'address1' }
          ]
        },
        {
          label: '住所2', headerClass: 'padding',
          properties: [
            { path: 'address2', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'max:256',
              validateAs: '住所2', editableId: 'address2' }
          ]
        },
        {
          label: '電話番号', headerClass: 'padding',
          properties: [
            { path: 'phone', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'max:256',
              validateAs: '電話番号', editableId: 'phone' }
          ]
        },
        {
          label: '理由',
          properties: [
            { path: 'reason', elementType: ELEMENT_TYPE.SELECT_BOX, type: 'String', validateRule: 'required',
              selectCode: 'd_stock_move.reason', codeName: 'd_stock_move.reason',
              validateAs: '理由', editableId: 'reason' }
          ]
        },
        {
          label: 'メモ',
          properties: [
            { path: 'note', elementType: ELEMENT_TYPE.INPUT, type: 'String', validateRule: 'max:256',
              validateAs: 'メモ', editableId: 'note' }
          ]
        },
        {
          label: '出荷希望日',
          properties: [
            { type: 'Date', elementType: ELEMENT_TYPE.DATE, path: 'preferredShippingAt',
              editableId: 'preferredShippingAt', dateFormat: 'YYYY/MM/DD' }
          ]
        }
      ]
    },
    _tableSetList2 () {
      if (this.mode === define.STOCK_MOVE_MODE.NORMAL) {
        return [
          { label: 'SKU', path: 'sku.code', type: 'String' },
          { label: '商品名', path: 'sku.name', type: 'String', width: 500 },
          { label: '在庫種別', path: '通常', type: 'String', elementType: ELEMENT_TYPE.FIXED_TEXT },
          { label: '指示前配分数', path: 'stockQty', type: 'Number', class: 'right' },
          { label: '移動数量', path: 'qty', type: 'Number', class: 'right' }
        ]
      } else {
        return [
          { label: 'SKU', path: 'sku.code', type: 'String' },
          { label: '商品名', path: 'sku.name', type: 'String', width: 150 },
          { label: '在庫種別', elementType: ELEMENT_TYPE.CELL,
            properties: [
              { path: 'normalStock.stockType', type: 'String' },
              { path: 'reservedStock.stockType', type: 'String' }
            ] },
          { label: '配分数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.amount', type: 'Number', class: 'right' },
              { path: 'reservedStock.amount', type: 'Number', class: 'right' }
            ] },
          { label: '公開数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.registered', type: 'Number', class: 'right' },
              { path: 'reservedStock.registered', type: 'Number', class: 'right' }
            ] },
          { label: '引当数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.allocated', type: 'Number', class: 'right' },
              { path: 'reservedStock.allocated', type: 'Number', class: 'right' }
            ] },
          { label: '注文数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.ordered', type: 'Number', class: 'right' },
              { path: 'reservedStock.ordered', type: 'Number', class: 'right' }
            ] },
          { label: '指図数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.exported', type: 'Number', class: 'right' },
              { path: 'reservedStock.exported', type: 'Number', class: 'right' }
            ] },
          { label: '販売可能数', elementType: ELEMENT_TYPE.CELL, class: 'right',
            properties: [
              { path: 'normalStock.qty', type: 'Number', class: 'right' },
              { path: 'reservedStock.qty', type: 'Number', class: 'right' }
            ] },
          { label: '移動数量', path: 'qty', type: 'Number', elementType: ELEMENT_TYPE.INPUT_NUMBER,
            editableId: 'qty', class: 'right', validateRule: 'required|min_value:1',
            validateAs: '移動数量', popoverErrorMsg: true, width: 80 },
          { type: 'Slot', path: 'operation', label: '', columnKey: 'operation', class: 'right',
            isShow: () => this.mode === define.STOCK_MOVE_MODE.NEW, width: 60 }
        ]
      }
    },
    isNew () {
      return this.mode === define.STOCK_MOVE_MODE.NEW
    },
    disabledAddButton () {
      return this.detailData.site.code === ''
    },
    stockMoveItems () {
      return this.detailData.stockMoveItem
    },
    disableSaveButton () {
      return !this.isChanged || (this.detailData.stockMoveItem || []).length < 1
    }
  },
  watch: {
    detailData: {
      handler: function (val, oldVal) {
        this.isChanged = this.$_ConfirmDiscardOnLeave_isChanged()
      },
      deep: true
    },
    async visible (val) {
      if (val) {
        // 詳細データ取得
        await this.getDetailData()
      }
      this.dialogVisible = val
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  created () {
  },
  methods: {
    /**
     * 移動指示詳細情報を取得します。
     */
    async getDetailData () {
      try {
        this.$loadingService.show()
        if (this.mode === define.STOCK_MOVE_MODE.NORMAL) {
          // 照会する場合、データを抽出
          const searchQuery = common.addGraphQlCondition(query.GRAPH_QL_QUERY_STOCK_MOVE_LIST, { uuid: this.uuid })
          const res = await common.postGraphQl(searchQuery)
          // 結果が null の場合
          if (res.data.data.stockMoveList.edges.length < 1) {
            this.$notify.error({
              message: 'データを取得できませんでした。'
            })
            // エラーの時、ダイアログ画面クローズ
            this.dialogVisible = false
            return
          }
          // DBから詳細画面の情報を取得
          this.detailData = res.data.data.stockMoveList.edges[0].node
        } else {
          // 新規の場合、画面各種データの初期化
          let delaultSite = ''
          // 選択肢が1つしかない場合は自動選択
          if (this.sites.length === 1) {
            delaultSite = this.sites[0].code
          }
          this.detailData = {
            codeName: '', // 出荷先
            code: '', // 出荷先コード
            name: '', // 出荷先名称
            postalCode: '', // 郵便番号
            stateCode: '', // 都道府県
            city: '', // 市区町村
            address1: '', // 住所1
            address2: '', // 住所2
            phone: '', // 電話番号
            reason: '', // 理由
            note: '', // メモ
            preferredShippingAt: '', // 出荷希望日
            site: { code: delaultSite }, // 在庫元
            stockMoveItem: [] // SKUリスト
          }
          // 新規のみ、変更監視を起動
          this.$_ConfirmDiscardOnLeave_watchStart(this.detailData)
        }
      } catch (error) {
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    /**
     * SKUに対して在庫情報を取得します。
     * @param {*} productId SKU
     */
    async getStockData (productId) {
      try {
        this.$loadingService.show()
        // 在庫情報取得Queryを作成
        const excQuery = common.addGraphQlCondition(query.GRAPH_QL_QUERY_EC_STOCK_LIST,
          {
            siteCodes: this.detailData.site.code, // サイト
            productIds: [productId], // SKU
            withEmpty: 1 })
        // 在庫情報取得
        const res = await common.postGraphQl(excQuery)
        const ecStockList = res.data.data.ecStockListItems.edges
        // 通常-在庫情報取得
        const normalStock =
          (ecStockList.find((v) => v.node.stockType === define.STOCK_TYPE.NORMAL) || {}).node
        // 予約-在庫情報取得
        const reservedStock =
          (ecStockList.find((v) => v.node.stockType === define.STOCK_TYPE.RESERVED) || {}).node
        return {
          normal: this.editStockData(normalStock, '通常'), // 通常-在庫情報
          reserved: this.editStockData(reservedStock, '予約') // 予約-在庫情報
        }
      } catch (error) {
        throw error
      } finally {
        this.$loadingService.close()
      }
    },
    /**
     * 在庫情報を編集します。
     * @param {*} stockData 在庫情報
     * @param {*} stockType 在庫種別
     * @return {*} 編集後在庫情報
     */
    editStockData (stockData, stockType) {
      let amount = 0
      let registered = 0
      let allocated = 0
      let ordered = 0
      let exported = 0
      let qty = 0
      if (stockData) {
        // 設定方針は在庫一覧と同じ
        amount = stockData.ecStockDistr ? stockData.ecStockDistr.amount : 0
        if (stockData.site.type === 7) {
          registered = '-'
          allocated = '-'
          ordered = '-'
          exported = '-'
          qty = '-'
        } else {
          registered = stockData.ecStock == null
            ? 0 : stockData.ecStock.registered == null
              ? '－' : Number(stockData.ecStock.registered)
          allocated = stockData.ecStock == null
            ? 0 : Number(stockData.ecStock.allocated)
          ordered = stockData.ecStock == null
            ? 0 : Number(stockData.ecStock.ordered)
          exported = stockData.ecStock == null
            ? 0 : Number(stockData.ecStock.exported)
          qty = stockData.ecStock == null
            ? 0 : stockData.ecStock.qty == null
              ? '-' : Number(stockData.ecStock.qty)
        }
      }
      return {
        stockType: stockType,
        amount: amount, // 配分数
        registered: registered, // 公開数
        allocated: allocated,   // 引当数
        ordered: ordered,       // 注文数
        exported: exported,     // 指図数
        qty: qty   // 販売可能数
      }
    },
    /**
     * 明細に商品情報を設定
     * @param {*} row 商品情報
     */
    async skuSelected (skuRow) {
      const code = _.get(skuRow, 'code') // SKU
      const name = _.get(skuRow, 'name') // 商品名
      // 既存SKUの場合、選択不可
      if (this.detailData.stockMoveItem.findIndex(e => e.sku.code === code) < 0) {
        // 参照用在庫情報取得
        const stockData = await this.getStockData(code)
        const row =
          {
            sku: {
              code: code, // SKU
              name: name // 商品名
            },
            normalStock: stockData.normal, // 通常在庫
            reservedStock: stockData.reserved, // 予約在庫
            qty: 0 // 数量

          }
        this.detailData.stockMoveItem.push(row)
      }
      // 商品検索ダイアログ画面クローズ
      this.searchSkusDialogVisible = false
    },
    /**
     * メイン情報に出荷先情報を設定
     * @param {*} row 出荷先情報
     */
    destinationSelected (row) {

      this.detailData.codeName =
        _.get(row, 'code') + ' - ' + _.get(row, 'name') // 出荷先
      this.detailData.code = _.get(row, 'code') // 出荷先名称
      this.detailData.name = _.get(row, 'name') // 出荷先名称
      this.detailData.postalCode = _.get(row, 'postalCode') // 郵便番号
      this.detailData.stateCode = _.get(row, 'stateCode') // 都道府県
      this.detailData.city = _.get(row, 'city') // 市区町村
      this.detailData.address1 = _.get(row, 'address1') // 住所1
      this.detailData.address2 = _.get(row, 'address2') // 住所2
      this.detailData.phone = _.get(row, 'phone') // 電話番号
      // 出荷先検索ダイアログ画面クローズ
      this.searchdestinationsDialogVisible = false
    },
    /**
     * Tooltipの項目を返却する.
     * @param {Object} row sku情報
     */
    getTooltipList (row) {
      return [
        {
          title: '削除',
          click: (item) => this.deleteRow(row.sku.code)
        }
      ]
    },
    /**
     * 行削除する.
     * @param {String} productId productId
     */
    deleteRow (productId) {
      const index = _.findIndex(this.detailData.stockMoveItem, (val) => val.sku.code === productId)
      this.detailData.stockMoveItem.splice(index, 1)
    },
    /**
     * 出荷先検索ダイアログ画面を開く.
     */
    openSearchdestination () {
      this.searchdestinationsDialogVisible = true
    },
    /**
     * 行追加する、商品検索ダイアログ画面を開く.
     */
    addRow () {
      this.searchSkusDialogVisible = true
    },
    /**
     * 保存処理.
     */
    async save () {
      // チェック処理
      if (!await this.$validator.validate()) {
        return
      }
      this.$confirm('保存しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(async () => {
        const msg = _.get(
          this.getServiceSiteConfig(null, 'd_stock_move', 'new', 'warn_message'), 'v.0', '0')
        let doNext = true
        if ((msg || '') !== '') {
          await this.$confirm(msg, define.MESSAGE_TITLE, {
            confirmButtonText: 'はい',
            cancelButtonText: 'いいえ',
            type: 'warning'
          }).then(async () => {
          }).catch(() => {
            doNext = false
            this.$notify.info('キャンセルしました。')
          })
        }
        if (doNext) {
          try {
            this.$loadingService.show()
            // 更新処理APIを呼び出す
            await this.callApi()
            // 一覧画面データに更新したデータを反映する
            this.$emit('refreshList')
            this.$notify.success('保存しました。')
            // ダイアログ画面の情報をクリア
            this.clear()
          } catch (error) {
            const data = _.get(error, 'response.data')
            if (data == null) {
              common.addError(error, { msg: message.MSG_502() })
            } else {
              common.addError(error, { msg: message.MSG_554() })
            }
            this.$notify.error({ error: error })
            throw error
          } finally {
            this.$loadingService.close()
          }
        }
      }).catch(() => {
        this.$notify.info('キャンセルしました。')
      })
    },
    /**
     * 更新 APIを呼び出す
     */
    async callApi () {
      let url = config.EC_STOCK_MOVE
      let method = 'post'
      const skus = []
      this.detailData.stockMoveItem.forEach((item) => {
        skus.push({
          skuCode: item.sku.code, // 商品ID(skuコード)
          qty: Number(item.qty) // 移動数量
        })
      })
      let data = {
        siteCode: this.detailData.site.code, // 対象在庫
        code: this.detailData.code, // 出荷先コード
        name: this.detailData.name, // 出荷先名称
        postalCode: this.detailData.postalCode, // 郵便番号
        stateCode: this.detailData.stateCode, // 都道府県
        city: this.detailData.city, // 市区町村
        address1: this.detailData.address1, // 住所1
        address2: this.detailData.address2, // 住所2
        phone: this.detailData.phone, // 電話番号
        reason: this.detailData.reason, // 理由
        note: this.detailData.note, // メモ
        preferredShippingAt: this.detailData.preferredShippingAt, // 出荷希望日
        skus: skus // 商品
      }
      // 更新APIを呼び出す
      await common.apiAxios(url, { method, data })
    },
    /**
     * 在庫元変更時、商品一覧をクリア
     */
    siteChange (key, oldKey) {
      if (this.detailData.stockMoveItem.length < 1) {
        return
      }
      this.$confirm('入力した商品一覧を破棄しますか？', define.MESSAGE_TITLE, {
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
        type: 'warning'
      }).then(() => {
        this.detailData.stockMoveItem = []
      }).catch(() => {
        this.detailData.site.code = oldKey
        this.$notify.info('キャンセルしました。')
      })
    },
    /**
     * 画面情報をクリアする.
     */
    clear () {
      this.detailData = {
        site: {}, // 在庫元
        stockMoveItem: [] // SKUリスト
      }
      this.dialogVisible = false
      this.$_ConfirmDiscardOnLeave_watchEnd()
      this.isChanged = false
    },
    /**
     * 画面をクローズする.
     */
    close () {
      if (this.isChanged) {
        this.$confirm('入力を破棄しますか？', define.MESSAGE_TITLE, {
          confirmButtonText: 'はい',
          cancelButtonText: 'いいえ',
          type: 'warning'
        }).then(() => {
          this.clear()
          this.$validator.reset()
          this.$_ConfirmDiscardOnLeave_watchEnd()
          this.isChanged = false
        }).catch(() => {})
      } else {
        this.clear()
      }
    }
  }
}
</script>
